import { createAsyncThunk } from '@reduxjs/toolkit';
import orderService from '../../services/orderService';
import offlineManager from '../../services/offlineManager';
import orderMirror from '../../services/orderMirror';
import localDB from '../../services/localDB';
import { saveModOriginalStatus } from '../../utils/modStatusMeta';
import {
	upsertOrder,
	upsertOrders,
	updateOrderStatus as updateOrderStatusAction,
	startModification,
	finishModification,
	cancelModification,
	addItemToOrder,
	removeItemFromOrder,
	updateItemQuantity,
	updateItemPrice,
	updateItemOptions,
	markServed,
	removeOrderById,
} from './ordersSlice';

// ─── Order money math (single source of truth for optimistic totals) ─────────
// Every item-edit thunk used to recompute `total = subtotal + tax`, silently
// DROPPING the order's discount. Online the server response overwrote the bad
// number, but offline it went into the sync queue verbatim — 6 production
// orders (₺1,475) shipped with discount_amount recorded yet not subtracted
// from total. Mirrors backend orderController math exactly: discount clamped
// to subtotal, 10% tax on the after-discount amount only when the order is
// explicitly not tax-exempt, every step rounded to 2dp.
export const computeOrderTotals = (order, items) => {
	const list = Array.isArray(items) ? items : (order?.items || []);
	const subtotal = parseFloat(
		list.reduce((s, i) => s + (Number(i?.amount) || 0), 0).toFixed(2)
	);

	const rawType = order?.discountType || order?.discount_type || 'none';
	const type = (rawType === 'percentage' || rawType === 'percent')
		? 'percent'
		: (rawType === 'amount' ? 'amount' : 'none');
	const value = Math.max(0, Number(order?.discountValue ?? order?.discount_value) || 0);
	let discountAmount = 0;
	if (type === 'percent') {
		discountAmount = parseFloat(Math.min(subtotal * (value / 100), subtotal).toFixed(2));
	} else if (type === 'amount') {
		discountAmount = parseFloat(Math.min(value, subtotal).toFixed(2));
	}

	const afterDiscount = Math.max(0, subtotal - discountAmount);
	const tax = order?.taxExempt === false
		? parseFloat((afterDiscount * 0.1).toFixed(2))
		: 0;
	const total = parseFloat((afterDiscount + tax).toFixed(2));
	return { subtotal, discountAmount, tax, total };
};

// ─── Pending-delete queue (offline) ──────────────────────────────────────────
// When a server order is deleted while offline, we store its ID here so that
// loadInitialPOSData can flush the deletes the next time we're back online.
const PENDING_DELETES_KEY = 'pos_pending_order_deletes';

// Entries are `{ id, reasonCode, reason }`. Legacy entries are bare id
// STRINGS (queued by builds before deletion reasons existed) — normalizeq
// below keeps them working.
const queuePendingDelete = (orderId, reason = null) => {
	try {
		const existing = JSON.parse(localStorage.getItem(PENDING_DELETES_KEY) || '[]');
		const idStr = String(orderId);
		const already = existing.some((e) => String(e?.id ?? e) === idStr);
		if (!already) {
			existing.push(reason ? { id: idStr, ...reason } : { id: idStr });
			localStorage.setItem(PENDING_DELETES_KEY, JSON.stringify(existing));
		}
	} catch {}
};

// Single-flight + append-safe write-back. The old implementation did a
// read-modify-write with awaits in between: a delete queued WHILE a flush was
// in flight (three concurrent callers exist: sync runner, boot, the delete
// thunk itself) was clobbered when the flush wrote back its stale snapshot —
// the deleted order then resurrected on the next fetch (audit: critical,
// delete-switch). Now: concurrent calls share one flight, and the write-back
// re-reads the CURRENT queue and removes only the ids this flight actually
// resolved — entries appended mid-flight survive untouched.
let _pendingDeletesFlight = null;
export const flushPendingDeletes = async () => {
	if (_pendingDeletesFlight) return _pendingDeletesFlight;
	_pendingDeletesFlight = (async () => {
		try {
			const raw = JSON.parse(localStorage.getItem(PENDING_DELETES_KEY) || '[]');
			if (!raw.length) return;
			// Legacy entries were plain strings; new ones carry the delete reason.
			const entries = raw.map((e) =>
				typeof e === 'string' ? { id: e } : { id: String(e?.id || ''), reasonCode: e?.reasonCode, reason: e?.reason }
			).filter((e) => e.id);
			const resolved = new Set();
			for (const entry of entries) {
				try {
					await orderService.deleteOrder(
						entry.id,
						entry.reasonCode || entry.reason ? { reasonCode: entry.reasonCode, reason: entry.reason } : null
					);
					resolved.add(entry.id);
				} catch (err) {
					// 404 = already gone on the server — resolved. Anything else
					// stays queued (with its reason) for the next flush.
					if (err?.response?.status === 404) resolved.add(entry.id);
				}
			}
			// Append-safe write-back: drop ONLY what this flight resolved.
			const current = JSON.parse(localStorage.getItem(PENDING_DELETES_KEY) || '[]');
			const kept = current.filter((e) => !resolved.has(String(typeof e === 'string' ? e : e?.id)));
			if (kept.length > 0) {
				localStorage.setItem(PENDING_DELETES_KEY, JSON.stringify(kept));
			} else {
				localStorage.removeItem(PENDING_DELETES_KEY);
			}
		} catch {} finally {
			_pendingDeletesFlight = null;
		}
	})();
	return _pendingDeletesFlight;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Normalize branch id from Redux/context (string or { _id }) for API payloads */
function toBranchId(ref) {
	if (ref == null || ref === '') return undefined;
	if (typeof ref === 'object') return ref._id || ref.id;
	return ref;
}

/** Pull the order from Redux by _id, id, or (post-remap) its old localId */
function getOrderFromState(state, orderId) {
	let order = state.orders.entities[orderId];
	if (!order) {
		order = Object.values(state.orders.entities).find(
			o => String(o._id || o.id) === String(orderId)
		);
	}
	if (!order) {
		// The background pump can remap a local_ id to its server id while a
		// popup/modification still holds the local id — the remapped entity
		// keeps its localId field, so match on that before giving up.
		order = Object.values(state.orders.entities).find(
			o => o.localId && String(o.localId) === String(orderId)
		);
	}
	return order || null;
}

// ─── Create order ─────────────────────────────────────────────────────────────

export const createOrder = createAsyncThunk(
	'orders/createOrder',
	async (orderData, { rejectWithValue, getState }) => {
		try {
			const state = getState();
			const resolvedBranchId =
				toBranchId(state.branch.selectedBranchId) || toBranchId(orderData.branchId);
			if (!resolvedBranchId) {
				return rejectWithValue('Branch is required');
			}
			const orderPayload = {
				...orderData,
				branchId: resolvedBranchId,
				status: 'confirmed',
				currentModificationCycle: 0,
			};
			// Trace every entry into the createOrder thunk so we can see if
			// multiple Redux dispatches fired for the same cart (the suspected
			// path for the duplicate-order symptom). Stack capture pinpoints
			// the dispatcher.
			let _stack = '';
			try { _stack = (new Error('thunk-trace').stack || '').split('\n').slice(2, 6).join(' › '); } catch (_) {}
			console.warn('[create-order] thunk dispatched', {
				branchId: resolvedBranchId,
				incomingLocalId: orderData?.localId || orderData?.local_id || null,
				incomingId: orderData?._id || orderData?.id,
				itemCount: orderPayload.items?.length || 0,
				orderType: orderPayload.orderType,
				total: orderPayload.total,
				caller: _stack,
			});
			const order = await offlineManager.createOrder(orderPayload, resolvedBranchId);
			if (!order.currentModificationCycle) order.currentModificationCycle = 0;
			return order;
		} catch (error) {
			console.error('[create-order] thunk FAILED', {
				errStatus: error?.response?.status,
				errMsg: error?.response?.data?.message || error?.message,
			});
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create order');
		}
	}
);

// ─── Update order status ──────────────────────────────────────────────────────

export const updateOrderStatus = createAsyncThunk(
	'orders/updateOrderStatus',
	async ({ orderId, status, ...updates }, { rejectWithValue, dispatch, getState }) => {
		try {
			const state        = getState();
			const currentOrder = getOrderFromState(state, orderId);
			const updated      = await offlineManager.updateOrderStatus(orderId, status, currentOrder, updates);
			dispatch(updateOrderStatusAction({ orderId, status, ...updates }));
			return updated;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update order status');
		}
	}
);

// ─── Update full order ────────────────────────────────────────────────────────

export const updateOrder = createAsyncThunk(
	'orders/updateOrder',
	async ({ orderId, orderData }, { rejectWithValue, getState }) => {
		try {
			const state        = getState();
			const currentOrder = getOrderFromState(state, orderId);
			// Log when saving a completed order offline so we can verify modification data survives.
			if (offlineManager.shouldUseLocal() && orderData?.status === 'completed') {
				console.log('[updateOrder offline] completing order — currentOrder snapshot:', {
					orderId,
					currentOrderModCycle: currentOrder?.currentModificationCycle,
					currentOrderModHistLen: currentOrder?.modificationHistory?.length,
					currentOrderLocalId: currentOrder?.localId,
				});
			}
			const result       = await offlineManager.updateOrder(orderId, orderData, currentOrder);
			return result;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update order');
		}
	}
);

// ─── KDS operations ───────────────────────────────────────────────────────────

export const markOrderAsPreparing = createAsyncThunk(
	'orders/markOrder',
	async (orderId, { rejectWithValue, dispatch, getState }) => {
		try {
			dispatch(updateOrderStatusAction({ orderId, status: 'preparing' }));
			const state        = getState();
			const currentOrder = getOrderFromState(state, orderId);
			const updated      = await offlineManager.updateOrderStatus(orderId, 'preparing', currentOrder);
			if (!offlineManager.shouldUseLocal()) {
				dispatch(upsertOrder(updated));
			}
			return updated;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to mark order as preparing');
		}
	}
);

export const markOrderAsReady = createAsyncThunk(
	'orders/markOrder',
	async (orderId, { rejectWithValue, dispatch, getState }) => {
		try {
			dispatch(updateOrderStatusAction({ orderId, status: 'ready' }));
			const state        = getState();
			const currentOrder = getOrderFromState(state, orderId);
			const updated      = await offlineManager.updateOrderStatus(orderId, 'ready', currentOrder);
			if (!offlineManager.shouldUseLocal()) {
				dispatch(upsertOrder(updated));
			}
			return updated;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to mark order as ready');
		}
	}
);

export const markOrderAsServed = createAsyncThunk(
	'orders/markOrder',
	async (orderId, { rejectWithValue, dispatch }) => {
		try {
			const result = await dispatch(markOrderServed({ orderId, status: 'served' })).unwrap();
			return result;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || error.payload || 'Failed to mark order as served');
		}
	}
);

export const markOrderAsCompleted = createAsyncThunk(
	'orders/markOrder',
	async (orderId, { rejectWithValue, dispatch, getState }) => {
		try {
			dispatch(updateOrderStatusAction({ orderId, status: 'completed' }));
			const state        = getState();
			const currentOrder = getOrderFromState(state, orderId);
			const updated      = await offlineManager.updateOrderStatus(orderId, 'completed', currentOrder);
			return updated;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to mark order as completed');
		}
	}
);

// ─── Fetch orders ─────────────────────────────────────────────────────────────

// ─── Local-first hydration (shared by boot paint + offline fallback) ─────────
// The screen must be paintable from LOCAL sources alone: order mirror (every
// order the device knew about) + this device's unsynced queue + whatever is
// already in Redux. Strictly additive, deduped on BOTH identities (server id
// AND local id). Self-verifying: a FAILED storage read is retried, never
// treated as "empty" (Biryani 3913/3919 duplicate, 2026-08-11 boot race).
const ACTIVE_FOR_MERGE = ['confirmed', 'preparing', 'ready', 'served', 'billed'];

const activeFromEntities = (state) => {
	const stateEntities = state?.orders?.entities || {};
	return Object.values(stateEntities).filter((o) => {
		const oid = String(o?._id || o?.id || '');
		if (oid.startsWith('draft_')) return false;
		return ACTIVE_FOR_MERGE.includes(String(o?.status || '').toLowerCase());
	});
};

const mergeLocalActiveSources = async (branchId, reduxActive) => {
	const readOfflineSources = async () => {
		const [localRes, mirrorRes] = await Promise.allSettled([
			offlineManager.getLocalOrders(branchId),
			orderMirror.getActive(branchId),
		]);
		if (localRes.status === 'rejected') {
			console.error('[orders] offline hydration: local queue read FAILED', {
				errMsg: String(localRes.reason?.message || localRes.reason),
			});
		}
		if (mirrorRes.status === 'rejected') {
			console.error('[orders] offline hydration: mirror read FAILED', {
				errMsg: String(mirrorRes.reason?.message || mirrorRes.reason),
			});
		}
		return {
			localOrders: localRes.status === 'fulfilled' ? (localRes.value || []) : [],
			mirrorOrders: mirrorRes.status === 'fulfilled' ? (mirrorRes.value || []) : [],
			anyFailed: localRes.status === 'rejected' || mirrorRes.status === 'rejected',
		};
	};

	let { localOrders, mirrorOrders, anyFailed } = await readOfflineSources();
	for (let attempt = 1; attempt <= 3 && anyFailed; attempt++) {
		console.warn('[orders] offline hydration retry — storage not ready yet', { attempt });
		await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
		({ localOrders, mirrorOrders, anyFailed } = await readOfflineSources());
	}

	// Reads SUCCEEDED but everything is empty while the queue's own counter
	// says entries exist — storage answered before it finished opening.
	if (!anyFailed && localOrders.length === 0 && mirrorOrders.length === 0 && (reduxActive || []).length === 0) {
		try {
			const counts = await offlineManager.getPendingCounts(branchId);
			if ((counts?.orders || 0) > 0) {
				console.warn('[orders] offline hydration: empty reads but queue counter says orders exist — retrying', {
					pendingOrders: counts.orders,
				});
				await new Promise((resolve) => setTimeout(resolve, 2000));
				({ localOrders, mirrorOrders } = await readOfflineSources());
			}
		} catch (_) { /* counters are best-effort */ }
	}

	// Every source is filtered to renderable ACTIVE rows: no drafts (they are
	// ephemeral carts, never orders), no deleted rows, no terminal statuses.
	// The queue especially holds raw shadows of every offline write — without
	// this filter a completed/draft shadow could hydrate back onto the floor
	// (confirmed in the Phase A adversarial review).
	const isHydratable = (o) => {
		const oid = String(o?._id || o?.id || '');
		if (!oid || oid.startsWith('draft_')) return false;
		if (o?.isDraft === true) return false;
		if (o?.deletedAt || o?.deleted_at) return false;
		return ACTIVE_FOR_MERGE.includes(String(o?.status || '').toLowerCase());
	};

	const canonicalOf = new Map(); // any identity key → canonical key
	const ordersByKey = new Map(); // canonical key → order (last write wins)
	const keysOf = (o) => {
		const primary = String(o?._id || o?.id || '');
		const local = String(o?.localId || o?.local_id || '');
		return [primary, local].filter(Boolean);
	};
	const putOrder = (o) => {
		if (!isHydratable(o)) return;
		const keys = keysOf(o);
		if (keys.length === 0) return;
		let canonical = null;
		for (const k of keys) {
			if (canonicalOf.has(k)) { canonical = canonicalOf.get(k); break; }
		}
		if (!canonical) canonical = keys[0];
		keys.forEach((k) => canonicalOf.set(k, canonical));
		ordersByKey.set(canonical, o);
	};
	// Source order matters: Redux LAST so the freshest in-memory copy always
	// wins over stale disk shadows (a queue row frozen at 'confirmed' must not
	// regress an order Redux already shows as 'served').
	(mirrorOrders || []).forEach(putOrder);
	(localOrders || []).forEach(putOrder);
	(reduxActive || []).forEach(putOrder);
	const merged = Array.from(ordersByKey.values());
	if (merged.length > 0) {
		console.warn('[orders] offline hydration — mirror + redux + local queue', {
			branchId,
			mirror: (mirrorOrders || []).length,
			redux: (reduxActive || []).length,
			local: (localOrders || []).length,
			merged: merged.length,
		});
	}
	return merged;
};

/**
 * LOCAL-FIRST PAINT: hydrate active orders from local sources and apply them
 * ADDITIVELY to Redux (upsertOrders — can only add/refresh, never remove).
 * Called at branch load BEFORE any network fetch so the floor renders its
 * occupancy from disk in well under a second; the server fetch that follows
 * reconciles to authoritative truth when (and if) it lands. Action type must
 * NOT start with 'orders/fetchOrders' — that prefix has setAll semantics in
 * the slice matcher.
 */
export const hydrateActiveOrdersFromLocal = createAsyncThunk(
	'orders/hydrateLocal',
	async (branchId, { getState, dispatch }) => {
		try {
			// Epoch fence: the storage reads below can take seconds (SQLite IPC
			// retries). If an authoritative server reconcile (setAll) lands while
			// we're reading, our snapshot is stale — applying it would resurrect
			// orders the server just told us are gone. Record the epoch now,
			// re-check before dispatching, drop silently if it moved.
			const epochAtStart = getState().orders?.reconcileEpoch ?? 0;
			const merged = await mergeLocalActiveSources(branchId, activeFromEntities(getState()));
			const epochNow = getState().orders?.reconcileEpoch ?? 0;
			if (epochNow !== epochAtStart) {
				console.log('[orders] local hydration dropped — authoritative reconcile landed during read', {
					epochAtStart, epochNow, staleCount: merged.length,
				});
				return { count: 0, dropped: true };
			}
			if (merged.length > 0) {
				dispatch(upsertOrders(merged));
			}
			return { count: merged.length };
		} catch (err) {
			console.warn('[orders] local hydration failed (non-fatal)', {
				errMsg: String(err?.message || err),
			});
			return { count: 0 };
		}
	}
);

export const fetchActiveOrders = createAsyncThunk(
	'orders/fetchOrders',
	async (branchId, { rejectWithValue, getState }) => {
		try {
			const fetched = await orderService.getActiveOrders(branchId);
			// A fetch can race a delete made moments ago on this device — the
			// response then still carries the deleted order, and setAll would
			// resurrect it on the table grid until the server's correction
			// arrives. Filter against the short-lived local delete tombstones.
			let orders = fetched;
			try {
				const { withoutRecentlyDeleted, withoutStaleActives } = await import('../../services/deletedLocalMarkers');
				// Same race for CHECKOUTS: a response predating a payment made
				// moments ago still carries the order as active — applying it
				// would re-occupy the just-freed table.
				orders = withoutStaleActives(withoutRecentlyDeleted(fetched));
			} catch (_) { /* tombstones are best-effort */ }
			// No manual mirroring needed here: the fulfilled action's setAll runs
			// through orderMirrorMiddleware, which mirrors every changed entity.
			// But the mirror also needs the REMOVALS this list implies: a synced
			// mirror row absent from this complete server list was closed on
			// another device — tombstone it so local hydration can't resurrect
			// it as a ghost occupied table. Fire-and-forget; never blocks paint.
			// MUST see the UNFILTERED server list: a row we hid above only
			// because of a local tombstone is still active server-side —
			// mirror-tombstoning it would be deletion on inference.
			orderMirror.tombstoneAbsent(fetched, branchId);
			return orders;
		} catch (error) {
			// ONLY fall back to local IndexedDB orders on true network failures
			// (offline / DNS / timeout). A 5xx from the server means the request
			// reached the origin but failed — the current in-memory Redux state
			// is more reliable than what's cached locally. Kalkinma 2026-07-22:
			// a Render 502 during a socket-reconnect refresh caused this catch
			// to replace Redux with an 8-draft local snapshot, wiping every
			// confirmed dine-in order → all tables looked "empty" to the cashier
			// (perceived as "tables vanished"). Rejecting cleanly leaves the
			// existing orders slice untouched — the .rejected reducer branch
			// only sets `error`/`loading=false`, it doesn't reset entities.
			const status = error?.response?.status;
			const isServerError = typeof status === 'number' && status >= 500 && status < 600;
			const isHttpError   = typeof status === 'number' && status >= 400;
			if (!isHttpError) {
				try {
					// Merge THREE offline sources, lowest priority first:
					//   1. the order mirror (every order the device knew about)
					//   2. this device's unsynced local queue (may carry offline
					//      edits the mirror predates)
					//   3. the CURRENT Redux entities — the freshest in-memory view,
					//      including socket updates; wins ties and guarantees this
					//      fallback is strictly ADDITIVE, never showing fewer orders
					//      than are already on screen (zaidm test 2026-07-27: mirror
					//      came back empty on a machine whose SQLite was broken, and
					//      the old 2-source merge replaced 42 on-screen orders with
					//      the 1 queued local order — every occupied table went
					//      "available" except the offline one)
					// Dedup on BOTH identities — server id AND local id — so an
					// order that synced under a server id can never appear a second
					// time via a stale local-keyed copy.
					const merged = await mergeLocalActiveSources(branchId, activeFromEntities(getState()));
					if (merged.length > 0) {
						return merged;
					}
				} catch (_) { /* non-fatal — fall through to rejectWithValue */ }
			} else if (isServerError) {
				console.warn('[orders] fetchActiveOrders got server error — keeping current state', {
					status, msg: error?.response?.data?.message,
				});
			}
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch orders');
		}
	}
);

export const fetchOrdersByTable = createAsyncThunk(
	'orders/fetchOrdersByTable',
	async (tableId, { rejectWithValue }) => {
		try {
			const response = await orderService.getOrdersByTable(tableId);
			return Array.isArray(response) ? response : response.orders || [];
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch table orders');
		}
	}
);

// ─── Delete order ─────────────────────────────────────────────────────────────

export const deleteOrder = createAsyncThunk(
	'orders/deleteOrder',
	async (arg, { rejectWithValue, getState, dispatch }) => {
		// Accepts either a bare id (draft cleanup and other internal callers) or
		// `{ orderId, reasonCode, reason }` from the staff-facing delete, which
		// requires an explanation.
		const rawId = typeof arg === 'object' && arg !== null ? (arg.orderId ?? arg.id) : arg;
		const reasonPayload =
			typeof arg === 'object' && arg !== null && (arg.reasonCode || arg.reason)
				? { reasonCode: arg.reasonCode || null, reason: arg.reason || null }
				: null;

		// Resolve a possibly-stale id FIRST. A delete dispatched with the old
		// local_ id after the pump's remap used to miss everything — the queue
		// row (re-keyed to the server id) survived and replayed, the mirror
		// kept the order, and it resurrected on the next hydration (audit:
		// critical, delete-switch). Resolve to the live entity; keep the old
		// id as an alias so pre-rekey artifacts die too.
		const entity = getOrderFromState(getState(), rawId);
		const orderId = String(entity?._id || entity?.id || rawId);
		const aliasId = (() => {
			const a = entity?.localId && String(entity.localId) !== orderId ? String(entity.localId) : null;
			if (a) return a;
			return String(rawId) !== orderId ? String(rawId) : null;
		})();
		// The fulfilled reducer removes only the payload id — sweep any entity
		// still keyed under the alias so a duplicate can't linger on screen.
		// Also RE-RESOLVE before returning: the pump can remap local_→server id
		// during this thunk's awaits, leaving a re-keyed entity our captured ids
		// no longer reach — kill that twin too (every call here is idempotent).
		const finishDelete = () => {
			if (aliasId) { try { dispatch(removeOrderById(aliasId)); } catch (_) {} }
			try {
				const live = getOrderFromState(getState(), rawId);
				const liveId = live ? String(live._id || live.id || '') : '';
				if (live && liveId && liveId !== orderId) {
					import('../../services/deletedLocalMarkers')
						.then(({ recordRecentDelete }) => recordRecentDelete(liveId))
						.catch(() => {});
					if (!liveId.startsWith('local_') && !liveId.startsWith('draft_')) {
						queuePendingDelete(liveId, reasonPayload);
						if (!offlineManager.shouldUseLocal()) flushPendingDeletes().catch(() => {});
					}
					// The mirror middleware ignores removeOrderById — cancel explicitly.
					try { orderMirror.mirrorOrders([{ ...live, status: 'cancelled' }]); } catch (_) {}
					localDB.deleteOrder(liveId).catch(() => {});
					dispatch(removeOrderById(liveId));
					console.warn('[delete-order] swept remapped twin after mid-thunk id remap', {
						requestedId: String(rawId), liveId,
					});
				}
			} catch (_) { /* sweep is best-effort */ }
			return orderId;
		};

		// Tombstone EVERY delete briefly so a stale server snapshot (reconnect
		// reconcile, racing fetch) can't resurrect the order on screen for a
		// second before the server's own deletion correction arrives.
		try {
			const { recordRecentDelete } = await import('../../services/deletedLocalMarkers');
			recordRecentDelete(orderId);
			if (aliasId) recordRecentDelete(aliasId);
		} catch (_) { /* tombstone is best-effort */ }

		// ── Local-first path (offline, or the Electron fleet — Phase B) ──────────
		if (offlineManager.shouldUseLocal() || offlineManager.localFirstWrites()) {
			// Remove from local storage immediately so it won't reappear after a
			// restart. The queue row may still sit under the pre-remap local_ key
			// (or vice versa), so clear the alias key as well.
			await localDB.deleteOrder(orderId).catch(() => {});
			if (aliasId) await localDB.deleteOrder(aliasId).catch(() => {});

			const idStr = String(orderId);
			if (!idStr.startsWith('local_') && !idStr.startsWith('draft_')) {
				// Server order: queue the API delete (carrying its reason) and, when
				// online, fire it now in the background — the UI never waits on it.
				queuePendingDelete(orderId, reasonPayload);
				// If the order was born local_ this session, a create replay may
				// still be in flight — mark the local id deleted too so the sync
				// endpoint kills any row it resolves by local_id.
				if (aliasId && aliasId.startsWith('local_')) {
					try {
						const { recordDeletedLocalId } = await import('../../services/deletedLocalMarkers');
						recordDeletedLocalId(aliasId);
					} catch (_) { /* marker is best-effort */ }
				}
				if (!offlineManager.shouldUseLocal()) {
					flushPendingDeletes().catch(() => {});
				}
			} else if (idStr.startsWith('local_')) {
				// The create may ALREADY be on its way to (or on) the server. Record
				// a marker; the next sync sends it and the SERVER resolves it by
				// local_id — otherwise a mistake order deleted seconds after placing
				// could survive server-side as a ghost table.
				try {
					const { recordDeletedLocalId } = await import('../../services/deletedLocalMarkers');
					recordDeletedLocalId(orderId);
					offlineManager.requestSync?.('delete-marker');
				} catch (_) { /* marker is best-effort */ }
			}

			// Return orderId as the fulfilled payload — the reducer removes it from Redux.
			return finishDelete();
		}

		// ── Online path (browser devices) ─────────────────────────────────────────

		// Local-only draft/offline orders have no server record — skip the API call
		// and clean up IndexedDB directly so the reducer can remove them from Redux.
		const idStr = String(orderId);
		if (idStr.startsWith('draft_') || idStr.startsWith('local_')) {
			await localDB.deleteOrder(orderId).catch(() => {});
			return finishDelete();
		}

		try {
			await orderService.deleteOrder(orderId, reasonPayload);
			return finishDelete();
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete order');
		}
	}
);

// ─── Order modification lifecycle ────────────────────────────────────────────

export const startOrderModification = createAsyncThunk(
	'orders/startModification',
	async (orderId, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();
			const order = getOrderFromState(state, orderId);
			if (!order) return rejectWithValue('Order not found');

			const originalStatus = order.status;
			dispatch(startModification({ orderId, originalStatus }));
			return { orderId, originalStatus, newStatus: originalStatus };
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to start modification');
		}
	}
);

// ─── Add item to order ────────────────────────────────────────────────────────

export const addItemToOrderThunk = createAsyncThunk(
	'orders/addItem',
	async ({ orderId, item, order: providedOrder }, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();
			const isModifyingThisOrder = state.orders?.modification?.activeOrderId
				? String(state.orders.modification.activeOrderId) === String(orderId)
				: false;

			let order = getOrderFromState(state, orderId);
			if (!order && providedOrder) {
				order = providedOrder;
				dispatch(upsertOrder(order));
			}
			if (!order) {
				try {
					const fetched = await orderService.getOrderById(orderId);
					order = fetched.order || fetched;
					dispatch(upsertOrder(order));
				} catch {
					return rejectWithValue('Order not found');
				}
			}
			if (!order) return rejectWithValue('Order not found');

			const menuItemId = item.menuItemId || item.id || item._id;
			const normalizedIncomingNote = String(item?.notes || '').trim();
			const incomingHasOptions = Array.isArray(item?.selectedOptions) && item.selectedOptions.length > 0;
			const incomingHasNote = normalizedIncomingNote.length > 0;

			// Bucket rule:
			// - Plain items (no options + no note) should merge into one plain bucket per menu item.
			// - Any item with options or note remains its own locked bucket.
			// - Items with different itemType (dine-in vs takeaway) must NOT merge.
			const incomingItemType = item.itemType || 'dine-in';
			const plainBucketIdx = (order.items || []).findIndex((i) => {
				const sameMenuItem = String(i.menuItemId || i.id || i._id) === String(menuItemId);
				const hasOptions = Array.isArray(i.selectedOptions) && i.selectedOptions.length > 0;
				const hasNote = String(i.notes || '').trim().length > 0;
				const isLocked = Boolean(i.isBucketLocked);
				const sameType = (i.itemType || 'dine-in') === incomingItemType;
				return sameMenuItem && !hasOptions && !hasNote && !isLocked && sameType;
			});

			let updatedItems;
			if (!incomingHasOptions && !incomingHasNote && plainBucketIdx >= 0) {
				const existing = order.items[plainBucketIdx];
				const newQty = (existing.quantity || 1) + (item.quantity || 1);
				const itemId = existing.cartItemId || existing._id || existing.id || existing.menuItemId || menuItemId;
				dispatch(updateItemQuantity({ orderId, itemId, quantity: newQty }));
				updatedItems = [...(order.items || [])];
				updatedItems[plainBucketIdx] = {
					...existing,
					quantity: newQty,
					amount: (existing.price || 0) * newQty,
				};
			} else {
				const cartItemId =
					item.cartItemId || `cart_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
				const newItem = {
					...item,
					menuItemId,
					cartItemId,
					isBucketLocked: Boolean(item.isBucketLocked),
					addedAt: item.addedAt || new Date().toISOString(),
					modificationCycle: item.modificationCycle ?? (order.currentModificationCycle || 0),
				};
				updatedItems = [...(order.items || []), newItem];
				dispatch(addItemToOrder({ orderId, item: newItem, modificationCycle: order.currentModificationCycle || 0 }));
			}

			const { subtotal, discountAmount, tax, total } = computeOrderTotals(order, updatedItems);
			dispatch(upsertOrder({ ...order, items: updatedItems, subtotal, discountAmount, tax, total }));

			// While modifying or draft — keep change in Redux only (no backend round-trip).
			// But if we are offline we ALSO write to IndexedDB so the change survives an
			// app restart before the modification is formally finished.
			// draft_/local_ ids don't exist on the server — a PUT would 404 and the
			// old fallback then seeded the sync queue with a stale draft (ghost-order
			// source). Route ALL localish ids away from the server path.
			if (isModifyingThisOrder || order.isDraft || String(orderId).startsWith('draft_') || String(orderId).startsWith('local_')) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || order;
				// local_ orders live only in localDB until synced — always persist the
				// edit there so the eventual sync uploads the current cart, not a stale one.
				if (offlineManager.shouldUseLocal() || String(orderId).startsWith('local_')) {
					await offlineManager.persistOrderLocally(latestOrder);
				}
				return latestOrder;
			}

			// Non-draft, non-modifying: persist to backend OR local
			if (offlineManager.shouldUseLocal()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			// Local-first (Electron, Phase B): the merged order persists to disk —
			// durable before any network — and the background pump replays it.
			// Browser devices keep the direct PUT below (their modification slips
			// print via the live socket emit, which sync replays suppress).
			if (offlineManager.localFirstWrites()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			// Online: fire to server with network-error fallback
			try {
				const backendResp = await orderService.updateOrder(orderId, { items: updatedItems });
				const backendOrder = backendResp.order || backendResp;
				dispatch(upsertOrder(backendOrder));
				return backendOrder;
			} catch (apiErr) {
				// A 4xx means the server answered and refused (e.g. 404 for an id it
				// never had) — persisting that order locally would seed the sync queue
				// with a row the server already rejected, which is how ghost orders
				// were born. Only fall back to local when the server was unreachable.
				const httpStatus = apiErr?.response?.status;
				if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
					return rejectWithValue(apiErr.response?.data?.message || apiErr.message || 'Failed to update order');
				}
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder).catch(() => {});
				return latestOrder;
			}
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to add item');
		}
	}
);

// ─── Remove item from order ───────────────────────────────────────────────────

export const removeItemFromOrderThunk = createAsyncThunk(
	'orders/removeItem',
	async ({ orderId, itemId }, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();
			const isModifyingThisOrder = state.orders?.modification?.activeOrderId
				? String(state.orders.modification.activeOrderId) === String(orderId)
				: false;
			const order = getOrderFromState(state, orderId);
			if (!order) return rejectWithValue('Order not found');

			const _removed = (order.items || []).find(i =>
				String(i.cartItemId || i._id || i.id || i.menuItemId) === String(itemId)
			);
			console.warn('[delete-item] start', {
				orderId,
				itemId,
				itemName: _removed?.name,
				itemsBefore: order.items?.length || 0,
				modifying: isModifyingThisOrder,
				offline: offlineManager.shouldUseLocal(),
			});

			dispatch(removeItemFromOrder({ orderId, itemId }));

			const updatedItems = (order.items || []).filter(i =>
				String(i.cartItemId || i._id || i.id || i.menuItemId) !== String(itemId)
			);
			const { subtotal, discountAmount, tax, total } = computeOrderTotals(order, updatedItems);
			dispatch(upsertOrder({ ...order, items: updatedItems, subtotal, discountAmount, tax, total }));

			// draft_/local_ ids don't exist on the server — a PUT would 404 and the
			// old fallback then seeded the sync queue with a stale draft (ghost-order
			// source). Route ALL localish ids away from the server path.
			if (isModifyingThisOrder || order.isDraft || String(orderId).startsWith('draft_') || String(orderId).startsWith('local_')) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || order;
				// local_ orders live only in localDB until synced — always persist the
				// edit there so the eventual sync uploads the current cart, not a stale one.
				if (offlineManager.shouldUseLocal() || String(orderId).startsWith('local_')) {
					await offlineManager.persistOrderLocally(latestOrder);
				}
				return latestOrder;
			}

			if (offlineManager.shouldUseLocal()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			// Local-first (Electron, Phase B): persist + pump, same as addItem.
			if (offlineManager.localFirstWrites()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				console.warn('[delete-item] success local-first', { orderId, itemId });
				return latestOrder;
			}

			// Online: fire to server with network-error fallback
			try {
				const backendResp  = await orderService.updateOrder(orderId, { items: updatedItems });
				const backendOrder = backendResp.order || backendResp;
				dispatch(upsertOrder(backendOrder));
				console.warn('[delete-item] success online', { orderId, itemId });
				return backendOrder;
			} catch (apiErr) {
				// A 4xx is a definitive server rejection — do NOT persist locally,
				// that path used to seed the sync queue with ghost orders.
				const httpStatus = apiErr?.response?.status;
				if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
					console.warn('[delete-item] server rejected — not queuing locally', {
						orderId, itemId, errStatus: httpStatus,
					});
					return rejectWithValue(apiErr.response?.data?.message || apiErr.message || 'Failed to remove item');
				}
				// Server unreachable — fall back to local so the edit isn't lost
				console.warn('[delete-item] server unreachable — queued locally', {
					orderId,
					itemId,
					errCode: apiErr?.code,
					errMsg: apiErr?.message,
				});
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder).catch(() => {});
				return latestOrder;
			}
		} catch (error) {
			console.error('[delete-item] FAILED', {
				orderId,
				itemId,
				errStatus: error?.response?.status,
				errMsg: error?.response?.data?.message || error?.message,
			});
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to remove item');
		}
	}
);

// ─── Update item quantity ─────────────────────────────────────────────────────

export const updateItemQuantityThunk = createAsyncThunk(
	'orders/updateQuantity',
	async ({ orderId, itemId, quantity }, { rejectWithValue, dispatch, getState }) => {
		try {
			if (quantity <= 0) {
				return dispatch(removeItemFromOrderThunk({ orderId, itemId }));
			}

			const state = getState();
			const isModifyingThisOrder = state.orders?.modification?.activeOrderId
				? String(state.orders.modification.activeOrderId) === String(orderId)
				: false;
			const order = getOrderFromState(state, orderId);
			if (!order) return rejectWithValue('Order not found');

			dispatch(updateItemQuantity({ orderId, itemId, quantity }));

			const updatedItems = (order.items || []).map(i =>
				String(i.cartItemId || i._id || i.id || i.menuItemId) === String(itemId)
					? { ...i, quantity, amount: (i.price || 0) * quantity }
					: i
			);
			const { subtotal, discountAmount, tax, total } = computeOrderTotals(order, updatedItems);
			dispatch(upsertOrder({ ...order, items: updatedItems, subtotal, discountAmount, tax, total }));

			// draft_/local_ ids don't exist on the server — a PUT would 404 and the
			// old fallback then seeded the sync queue with a stale draft (ghost-order
			// source). Route ALL localish ids away from the server path.
			if (isModifyingThisOrder || order.isDraft || String(orderId).startsWith('draft_') || String(orderId).startsWith('local_')) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || order;
				// local_ orders live only in localDB until synced — always persist the
				// edit there so the eventual sync uploads the current cart, not a stale one.
				if (offlineManager.shouldUseLocal() || String(orderId).startsWith('local_')) {
					await offlineManager.persistOrderLocally(latestOrder);
				}
				return latestOrder;
			}

			if (offlineManager.shouldUseLocal()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			// Local-first (Electron, Phase B): persist + pump, same as addItem.
			if (offlineManager.localFirstWrites()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			// Online: fire to server with network-error fallback
			try {
				const backendResp  = await orderService.updateOrder(orderId, { items: updatedItems });
				const backendOrder = backendResp.order || backendResp;
				dispatch(upsertOrder(backendOrder));
				return backendOrder;
			} catch (apiErr) {
				// A 4xx means the server answered and refused (e.g. 404 for an id it
				// never had) — persisting that order locally would seed the sync queue
				// with a row the server already rejected, which is how ghost orders
				// were born. Only fall back to local when the server was unreachable.
				const httpStatus = apiErr?.response?.status;
				if (httpStatus && httpStatus >= 400 && httpStatus < 500) {
					return rejectWithValue(apiErr.response?.data?.message || apiErr.message || 'Failed to update order');
				}
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder).catch(() => {});
				return latestOrder;
			}
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update quantity');
		}
	}
);

// ─── Update a single cart item's unit price ──────────────────────────────────
export const updateItemPriceThunk = createAsyncThunk(
	'orders/updateItemPrice',
	async ({ orderId, itemId, price }, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();
			const order = getOrderFromState(state, orderId);
			if (!order) return rejectWithValue('Order not found');

			// Optimistic update
			dispatch(updateItemPrice({ orderId, itemId, price }));

			const updatedItems = (order.items || []).map(i =>
				String(i.cartItemId || i._id || i.id || i.menuItemId) === String(itemId)
					? { ...i, price, amount: price * (i.quantity || 1) }
					: i
			);
			const { subtotal, discountAmount, tax, total } = computeOrderTotals(order, updatedItems);
			dispatch(upsertOrder({ ...order, items: updatedItems, subtotal, discountAmount, tax, total }));

			// Route localish ids (draft_/local_) to local persistence — a PUT
			// with a draft/local id 404s. Missing `local_` catch was the driver
			// of the draft_ 404 storm at Ayder on 2026-07-18 (v1.0.27 build).
			if (offlineManager.shouldUseLocal() || order.isDraft
			  || String(orderId).startsWith('draft_') || String(orderId).startsWith('local_')) {
				const updatedState = getState();
				const latestOrder = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder).catch(() => {});
				return latestOrder;
			}

			// Local-first (Electron, Phase B): persist + pump, same as addItem.
			if (offlineManager.localFirstWrites()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			try {
				const backendResp = await orderService.updateOrder(orderId, { items: updatedItems });
				const backendOrder = backendResp.order || backendResp;
				dispatch(upsertOrder(backendOrder));
				return backendOrder;
			} catch (apiErr) {
				const updatedState = getState();
				const latestOrder = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder).catch(() => {});
				return latestOrder;
			}
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update price');
		}
	}
);

// ─── Update item-level custom options (modifiers) ────────────────────────────
export const updateItemOptionsThunk = createAsyncThunk(
	'orders/updateItemOptions',
	async ({ orderId, itemId, selectedOptions, note }, { rejectWithValue, dispatch, getState }) => {
		try {
			const state = getState();
			const order = getOrderFromState(state, orderId);
			if (!order) return rejectWithValue('Order not found');

			// Optimistic update in Redux immediately
			dispatch(updateItemOptions({ orderId, itemId, selectedOptions, note }));

			const normalizedNote = typeof note === 'string' ? note.trim() : '';
			const updatedItems = (order.items || []).map(i =>
				String(i.cartItemId || i._id || i.id || i.menuItemId) === String(itemId)
					? { ...i, selectedOptions, notes: normalizedNote, isBucketLocked: true }
					: i
			);

			// Persist — offline OR localish-id (draft_/local_) uses IndexedDB;
			// only real server-issued ids get the PUT. Fires draft_ → 404 storm
			// before this guard was added (Ayder 2026-07-18, v1.0.27).
			if (offlineManager.shouldUseLocal() || order.isDraft
			  || String(orderId).startsWith('draft_') || String(orderId).startsWith('local_')) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			// Local-first (Electron, Phase B): persist + pump, same as addItem.
			if (offlineManager.localFirstWrites()) {
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder);
				return latestOrder;
			}

			// Online: update items on server (fire-and-forget style, non-fatal)
			try {
				const backendResp  = await orderService.updateOrder(orderId, { items: updatedItems });
				const backendOrder = backendResp.order || backendResp;
				dispatch(upsertOrder(backendOrder));
				return backendOrder;
			} catch (apiErr) {
				// Persist locally so options aren't lost if the API call fails transiently
				const updatedState = getState();
				const latestOrder  = getOrderFromState(updatedState, orderId) || { ...order, items: updatedItems };
				await offlineManager.persistOrderLocally(latestOrder).catch(() => {});
				return latestOrder;
			}
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update item options');
		}
	}
);

// ─── Helpers for offline modification description ─────────────────────────────

function generateOfflineModDescription(added, removed, quantityChanged) {
	const parts = [];
	if (added.length)          parts.push(`Added ${added.map(i => `${i.quantity}x ${i.name}`).join(', ')}`);
	if (removed.length)        parts.push(`Removed ${removed.map(i => `${i.quantity}x ${i.name}`).join(', ')}`);
	if (quantityChanged.length) parts.push(`Changed qty: ${quantityChanged.map(i => `${i.name} (${i.oldQuantity}→${i.newQuantity})`).join(', ')}`);
	return parts.join('; ') || 'Order modified';
}

// ─── Finish modification ──────────────────────────────────────────────────────

export const finishOrderModification = createAsyncThunk(
	'orders/finishModification',
	async (orderId, { rejectWithValue, dispatch, getState }) => {
		try {
			const state        = getState();
			const modification = state.orders.modification;
			// Alias-resolved guard: the pump can remap local_→server id mid-
			// modification — remapOrderIds re-keys activeOrderId while the caller
			// may still hold the pre-remap id. Compare resolved ENTITIES, not raw
			// ids, then continue under the canonical id.
			const guardTarget = getOrderFromState(state, orderId);
			const guardActive = getOrderFromState(state, modification.activeOrderId);
			if (!guardTarget || !guardActive || guardTarget !== guardActive) {
				return rejectWithValue('Order is not being modified');
			}
			orderId = String(guardTarget._id || guardTarget.id || orderId);

			const originalStatus = modification.originalStatus;
			const originalItems  = modification.originalItems || [];
			const order          = getOrderFromState(state, orderId);
			if (!order) return rejectWithValue('Order not found');

			console.warn('[modify-order] finish start', {
				orderId,
				itemsBefore: originalItems.length,
				itemsAfter: order.items?.length || 0,
				cartBefore: (originalItems || []).map(i => ({
					name: i.name || i.title,
					qty:  i.quantity,
					amt:  i.amount,
				})),
				cartAfter: (order.items || []).map(i => ({
					name: i.name || i.title,
					qty:  i.quantity,
					amt:  i.amount,
				})),
				offline: offlineManager.shouldUseLocal(),
			});

			// Local-first (Electron, Phase B) takes the same branch as offline:
			// the modification entry (diff, snapshot, description) is built
			// CLIENT-side, persisted durably, and replayed by the pump — one
			// history author instead of the old online/offline divergence where
			// the server generated a differently-worded entry. Modification
			// slips print locally on Electron, so no print behavior changes.
			if (offlineManager.shouldUseLocal() || offlineManager.localFirstWrites()) {
				const now         = new Date().toISOString();
				const newCycle    = (order.currentModificationCycle || 0) + 1;
				const currentItems = order.items || [];

				// ── Compute item-level diff so the history tab shows exactly what changed ──
				const added           = [];
				const removed         = [];
				const quantityChanged = [];

				// Match by cartItemId first so two lines with the same menuItemId
				// (e.g. duplicate Chicken Biryani lines) don't collide and produce a
				// phantom quantityChanged entry that prints a wrong CANCELLED slip.
				const normalize = (i) => String(i.cartItemId || i.menuItemId || i._id || i.id || '');

				for (const curr of currentItems) {
					const orig = originalItems.find(i => normalize(i) === normalize(curr));
					if (!orig) {
						added.push({ name: curr.name, quantity: curr.quantity, amount: curr.amount || 0 });
					} else if (orig.quantity !== curr.quantity) {
						quantityChanged.push({ name: curr.name, oldQuantity: orig.quantity, newQuantity: curr.quantity });
					}
				}
				for (const orig of originalItems) {
					const curr = currentItems.find(i => normalize(i) === normalize(orig));
					if (!curr) removed.push({ name: orig.name, quantity: orig.quantity, amount: orig.amount || 0 });
				}

				const modEntry = {
					modifiedAt:        now,
					modified_at:       now,
					modificationType:  'order_modification',
					modification_type: 'order_modification',
					modificationCycle:  newCycle,
					modificationNumber: newCycle,
					itemCount:          currentItems.length,
					description:        generateOfflineModDescription(added, removed, quantityChanged),
					// Top-level originalStatus lets getOrderDisplayStatus detect the
					// "billed → modify → re-bill" case without digging into the snapshot.
					originalStatus,
					original_status:   originalStatus,
					changes: {
						items: { added, removed, quantityChanged, priceChanged: [] },
					},
					originalOrderSnapshot: {
						items:    originalItems,
						subtotal: order.subtotal,
						total:    order.total,
						status:   originalStatus,
					},
				};

				const wasAlreadyBilled = originalStatus === 'billed' || !!(order.billedAt || order.billed_at);
				const updatedOrder = {
					...order,
					// Drop the transient "no print" flag — the local ticket already
					// excluded those items; it must not persist on the saved order.
					items: (order.items || []).map(({ noPrint, ...rest }) => rest),
					currentModificationCycle:   newCycle,
					current_modification_cycle: newCycle,
					modificationHistory: [
						...(order.modificationHistory || order.modification_history || []),
						modEntry,
					],
					// Flag: permanently mark post-billed modification for history tab
					...(wasAlreadyBilled ? { modifiedAfterBilled: true, modified_after_billed: true } : {}),
					// LOCAL-FIRST PROTECTION. persistOrderLocally flags the localDB row
					// _isLocal, but the rollback guards (fetch preserve-filter, socket
					// freshness guard, reconcile apply, upsertOrders) all key off the
					// REDUX entity's _isLocal. Without it here a background fetch or a
					// popup-reopen re-fetch overwrote the fresh modification with the
					// stale pre-modification server row until the pump caught up
					// ("reopen shows the old cart, takes time to update"). clearLocalFlags
					// drops this on server ack, so it never shadows real later truth.
					_isLocal: true,
					updatedAt: now,
				};

				// Persist originalStatus to localStorage so getOrderDisplayStatus can
				// detect the "billed → modify → re-bill" scenario after Redux re-fetches.
				saveModOriginalStatus(orderId, newCycle, originalStatus);

				await offlineManager.persistOrderLocally(updatedOrder);
				dispatch(upsertOrder(updatedOrder));
				dispatch(finishModification({ orderId, finalStatus: originalStatus }));
				return { orderId, finalStatus: originalStatus };
			}

			const itemsPayload = (order.items || []).map(i => ({
				menuItemId: i.menuItemId || i.id || i._id,
				// Carry cartItemId so the backend can distinguish two lines with the same
				// menuItemId (e.g. two Chicken Biryani lines with the same options). Without
				// this, the backend's oldItemMap collapses both lines and the next
				// modification diff reads a phantom "qty 3 → 1" → CANCELLED slip prints.
				...(i.cartItemId ? { cartItemId: i.cartItemId } : {}),
				...(Array.isArray(i.selectedOptions) && i.selectedOptions.length > 0
					? { selectedOptions: i.selectedOptions }
					: {}),
				...(i.notes ? { notes: i.notes } : {}),
				quantity:   i.quantity || 1,
				status:     i.status,
				itemType:   i.itemType || 'dine-in',
				// Carry the transient "no print" flag so the backend includes it in
				// the order_updated socket emit → the remote print station skips it.
				...(i.noPrint ? { noPrint: true } : {}),
				// Full display fields MUST travel too. The online PUT path rebuilds
				// them from menu_items anyway, but when this modify fails over to the
				// OFFLINE queue, sync replays the payload verbatim into the server's
				// items — a skeleton payload wiped names/prices off Istanbul order
				// #1275 (staff saw "Unnamed Item / TRY 0.00" on every device).
				name:   i.name || i.title || '',
				price:  parseFloat(i.price) || 0,
				amount: parseFloat(i.amount) || (parseFloat(i.price) || 0) * (i.quantity || 1),
				...(i.addedAt ? { addedAt: i.addedAt } : {}),
				...(i.modificationCycle !== undefined ? { modificationCycle: i.modificationCycle } : {}),
				...(i.isCounterItem !== undefined ? { isCounterItem: i.isCounterItem } : {}),
			}));

			// ── Optimistic update: close modification mode immediately so the UI
			//    responds instantly, then sync with the server in the background. ──
			dispatch(finishModification({ orderId, finalStatus: originalStatus }));

			// A modify session on a not-yet-synced order (draft_/local_ id) has
			// no server row to PUT against — persist locally and rely on sync to
			// replay. Otherwise the finish emits a doomed PUT that 404s and
			// leaves the modification unlogged server-side.
			if (String(orderId).startsWith('draft_') || String(orderId).startsWith('local_')) {
				const latestState = getState();
				const latestOrder = getOrderFromState(latestState, orderId);
				if (latestOrder) offlineManager.persistOrderLocally(latestOrder).catch(() => {});
				return { orderId, finalStatus: originalStatus };
			}

			// Route through offlineManager (NOT orderService directly): on a network
			// failure it persists the modification locally and queues it for sync
			// replay — the direct call used to just log "sync FAILED" and DROP the
			// modification, leaving the server with the old items until some later
			// action happened to re-send them (observed at Ayder, order #1012).
			const _latestForModSync = getState().orders.entities[orderId] || order;
			offlineManager.updateOrder(orderId, { items: itemsPayload }, _latestForModSync).then((resp) => {
				const serverOrder = resp.order || resp;

				// Patch originalStatus onto the newest modification history entry so
				// getOrderDisplayStatus can detect the "billed → modify → re-bill" case.
				const patchedHistory = (serverOrder.modificationHistory || serverOrder.modification_history || []).map(
					(entry, idx, arr) => {
						if (idx !== arr.length - 1) return entry;
						if (entry.originalStatus || entry.original_status) return entry;
						return {
							...entry,
							originalStatus,
							original_status: originalStatus,
							originalOrderSnapshot: entry.originalOrderSnapshot || entry.original_order_snapshot || {
								status: originalStatus,
							},
						};
					}
				);

				const updatedOrder = {
					...serverOrder,
					// "No print" is a transient per-print instruction — don't let it
					// linger on the cashier's order after the ticket has been sent.
					items: (serverOrder.items || []).map(({ noPrint, ...rest }) => rest),
					modificationHistory:  patchedHistory,
					modification_history: patchedHistory,
				};

				const newCycleOnline = updatedOrder.currentModificationCycle ?? updatedOrder.current_modification_cycle;
				saveModOriginalStatus(orderId, newCycleOnline, originalStatus);

				dispatch(upsertOrder(updatedOrder));
			}).catch((err) => {
				console.error('[modify-order] sync to server FAILED', {
					orderId,
					errStatus: err?.response?.status,
					errMsg: err?.response?.data?.message || err?.message,
				});
			});

			console.warn('[modify-order] finish success', { orderId, finalStatus: originalStatus });
			return { orderId, finalStatus: originalStatus };
		} catch (error) {
			console.error('[modify-order] finish FAILED', {
				orderId,
				errStatus: error?.response?.status,
				errMsg: error?.response?.data?.message || error?.message,
			});
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to finish modification');
		}
	}
);

// ─── Cancel modification ──────────────────────────────────────────────────────

export const cancelOrderModification = createAsyncThunk(
	'orders/cancelModification',
	async (orderId, { rejectWithValue, dispatch, getState }) => {
		try {
			const state        = getState();
			const modification = state.orders.modification;
			// Alias-resolved guard: the pump can remap local_→server id mid-
			// modification — remapOrderIds re-keys activeOrderId while the caller
			// may still hold the pre-remap id. Compare resolved ENTITIES, not raw
			// ids, then continue under the canonical id.
			const guardTarget = getOrderFromState(state, orderId);
			const guardActive = getOrderFromState(state, modification.activeOrderId);
			if (!guardTarget || !guardActive || guardTarget !== guardActive) {
				return rejectWithValue('Order is not being modified');
			}
			orderId = String(guardTarget._id || guardTarget.id || orderId);

			const originalStatus = modification.originalStatus;

			// Local-first (offline OR Electron): restore the pre-modification
			// snapshot — items, totals, status — into Redux AND local storage,
			// no server read needed. The old offline path reverted only the
			// STATUS: the half-modified cart stayed on screen and (for local_
			// orders) queued for sync, so a cancelled modification could still
			// upload its abandoned items.
			if (offlineManager.shouldUseLocal() || offlineManager.localFirstWrites()) {
				const order = getOrderFromState(state, orderId);
				const originalItems = modification.originalItems || [];
				if (order && originalItems.length > 0) {
					const { subtotal, discountAmount, tax, total } = computeOrderTotals(order, originalItems);
					const restored = {
						...order,
						items: originalItems,
						subtotal, discountAmount, tax, total,
						status: originalStatus,
					};
					dispatch(upsertOrder(restored));
					await offlineManager.persistOrderLocally(restored).catch(() => {});
				} else {
					dispatch(updateOrderStatusAction({ orderId, status: originalStatus }));
				}
				dispatch(cancelModification());
				return { orderId, originalStatus };
			}

			await dispatch(updateOrderStatus({ orderId, status: originalStatus }));
			dispatch(cancelModification());

			const order = await orderService.getOrderById(orderId);
			dispatch(upsertOrder(order));
			return { orderId, originalStatus };
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to cancel modification');
		}
	}
);

// ─── Mark order served ────────────────────────────────────────────────────────

export const markOrderServed = createAsyncThunk(
	'orders/markServed',
	async ({ orderId, status = 'served' }, { rejectWithValue, dispatch, getState }) => {
		try {
			const orderIdToUse  = typeof orderId === 'object' ? (orderId.id || orderId._id) : String(orderId);
			const state         = getState();
			const currentOrder  = getOrderFromState(state, orderIdToUse);

			const updatedOrder  = await offlineManager.markOrderServed(orderIdToUse, status, currentOrder);
			const finalOrderId  = updatedOrder.id || updatedOrder._id || orderIdToUse;

			if (updatedOrder.lastServedAt || updatedOrder.last_served_at) {
				const timestamp = updatedOrder.lastServedAt || updatedOrder.last_served_at;
				dispatch(markServed({ orderId: finalOrderId, timestamp }));
			}
			dispatch(upsertOrder(updatedOrder));
			return updatedOrder;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to mark order as served');
		}
	}
);

// ─── Fetch order history ──────────────────────────────────────────────────────

export const fetchOrderHistory = createAsyncThunk(
	'orders/fetchOrderHistory',
	async ({ branchId, page = 1, limit = 50, startDate = null, endDate = null, searchTerm = null, statusFilter = null, timezone = null }, { rejectWithValue }) => {
		try {
			const response = await orderService.getOrderHistory(branchId, page, limit, startDate, endDate, searchTerm, statusFilter, timezone);
			return {
				orders:     response.orders || [],
				pagination: response.pagination || { total: 0, page, pages: 0, totalPages: 0 },
			};
		} catch (error) {
			// ── Offline fallback: serve the CURRENT business day from the order
			// mirror. Same rule as fetchActiveOrders — network-class failures
			// only, never 4xx/5xx (the server answered; trust its error).
			const status = error?.response?.status;
			const isHttpError = typeof status === 'number' && status >= 400;
			if (!isHttpError) {
				try {
					const { businessDate, orders: dayOrders } = await orderMirror.getCurrentDay(branchId);
					if (Array.isArray(dayOrders) && dayOrders.length > 0) {
						// Match the server's history shape/filters as closely as the
						// mirror allows: newest first, optional status filter, one page.
						let list = dayOrders;
						if (statusFilter && statusFilter !== 'modified') {
							list = list.filter((o) => String(o?.status || '').toLowerCase() === String(statusFilter).toLowerCase());
						}
						if (searchTerm) {
							const q = String(searchTerm).toLowerCase();
							list = list.filter((o) =>
								String(o?.orderNumber || '').toLowerCase().includes(q) ||
								String(o?.tableNumber || '').toLowerCase().includes(q) ||
								String(o?._id || o?.id || '').toLowerCase().includes(q)
							);
						}
						list = [...list].sort((a, b) =>
							new Date(b?.createdAt || b?.created_at || 0) - new Date(a?.createdAt || a?.created_at || 0)
						);
						// Normalize identity + items: mirror payloads mix shapes (server
						// rows have _id, local/socket rows may only carry id/localId).
						// History renders assume _id and items exist — an _id-less row
						// crashed the POS to the recovery screen (Ayder 2026-08-09).
						list = list.map((o) => ({
							...o,
							_id: o?._id || o?.id || o?.localId || o?.local_id || null,
							id: o?.id || o?._id || o?.localId || o?.local_id || null,
							items: Array.isArray(o?.items) ? o.items : [],
						}));
						console.warn('[orders] history served OFFLINE from mirror', {
							branchId, businessDate, count: list.length,
						});
						return {
							orders: list,
							pagination: { total: list.length, page: 1, pages: 1, totalPages: 1 },
							offline: true,
							offlineBusinessDate: businessDate,
						};
					}
				} catch (_) { /* fall through to rejectWithValue */ }
			}
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch order history');
		}
	}
);
